import { useState, useCallback, useRef, useEffect } from 'react'
import {
  Box,
  Typography,
  TextField,
  Switch,
  Autocomplete,
  Alert,
} from '@mui/material'
import { STEPS } from '../onboardingSteps'
import { useOnboardingV2 } from '../OnboardingContext'
import { useWizardData, readWizardData } from '../hooks/useWizardData'
import { useStep } from '../hooks/useStep'
import { useToken } from '../hooks/useToken'
import {
  useUpdateOrg,
  useUpdateOrgWebsiteMutation,
  useCurrentUserOrgs,
} from '@/utils/hooks/orgs'
import { DuplicateOrgNameError } from '@/api/orgsAPI'
import { useDiscoverPages } from '../hooks/useDiscoverPages'
import { useCheckDuplicate } from '../hooks/useCheckDuplicate'
import { useRequestJoin } from '../hooks/useRequestJoin'
import { useSwitchToOrg } from '../hooks/useSwitchToOrg'
import {
  DuplicateWarning,
  INITIAL_DUPLICATE,
  type DuplicateState,
} from '../components/DuplicateWarning'
import {
  useRegionOpportunityCount,
  useRegions,
  useRegionSets,
} from '@/utils/hooks/regions'
import { useIwi } from '@/utils/hooks/iwi'
import { getCurrentUser } from '@/utils/auth/UserProvider'
import {
  stepHeadingSx,
  stepDescriptionSx,
  formLabelSx,
  inputSx,
  textFieldSx,
} from '../styles'
import { COLORS } from '../theme'
import LocationSelector, { isNational } from '@/components/LocationSelector'
import type { OrgFormData } from '../types'
import type { IwiData } from '@/models/Iwi'
import type { OrgData } from '@/models/Orgs'
import { normalizeUrl } from '../utils/normalizeUrl'
import { isBlockedUrl, getBlockedUrlMessage } from '@/utils/urlValidation'
import type { UUID } from 'crypto'

/** Build OrgFormData from an existing org record (DB is source of truth). */
function buildFormFromOrg(org: OrgData): OrgFormData {
  return {
    orgName: org.name ?? '',
    website: org.website ?? '',
    description: org.description ?? '',
    regionIds: org.region_ids ?? [],
    iwi: org.iwi ?? [],
    showIwiInput: (org.iwi?.length ?? 0) > 0,
    refuseGambling: org.accepts_gambling_proceeds === false,
    acceptInternational: org.accepts_international ?? false,
  }
}

export default function OrgDetails({
  section = 'details',
}: {
  // Which slice of the onboarding org form to render. 'details' = name +
  // website; 'locations' = funding preferences + location picker. Both share the
  // same form state + save (a single updateOrg with the whole form), so neither
  // step drops the other's fields.
  section?: 'details' | 'locations'
} = {}) {
  const { orgUuid, maxStepReached, staleFrom, setStaleFrom } = useOnboardingV2()
  const [wizardData, setWizardData] = useWizardData()
  const { getToken } = useToken()
  const { user, token: ctxToken } = getCurrentUser()
  const token = ctxToken ?? ''

  const updateOrg = useUpdateOrg()
  const updateWebsite = useUpdateOrgWebsiteMutation()
  const discoverPages = useDiscoverPages()
  const checkDuplicate = useCheckDuplicate()
  const requestJoinMutation = useRequestJoin()
  const switchToOrg = useSwitchToOrg()
  const { regions, isLoading: regionsLoading } = useRegions(token)
  const { regionSets } = useRegionSets(token)
  const { iwi: iwiList, isLoading: iwiLoading } = useIwi(token)

  // Fetch existing org record — DB is source of truth.
  // Picks up edits made in the main app between onboarding sessions.
  // orgUuid is always set by step 4 (OrgNumber creates the org).
  const { orgData } = useCurrentUserOrgs(
    token,
    user,
    orgUuid ? [orgUuid as UUID] : [],
  )
  const existingOrg = orgData.find((o) => o.uuid === orgUuid) ?? null

  const emptyForm: OrgFormData = {
    orgName: '',
    website: '',
    description: '',
    regionIds: [],
    iwi: [],
    showIwiInput: false,
    refuseGambling: false,
    acceptInternational: false,
  }
  const form: OrgFormData = wizardData.orgForm ?? emptyForm
  // Counter baseline: with no local region picked, show the nationally-available
  // count so the number visibly rises as the user adds local regions. Passing an
  // empty array would tell the API "no region filter" and return every
  // opportunity (the maximum), which is the opposite of the intended baseline.
  const nationalRegionId = (regions ?? []).find(isNational)?.id
  const counterRegionIds =
    form.regionIds.length > 0
      ? form.regionIds
      : nationalRegionId !== undefined
        ? [nationalRegionId]
        : []
  const { count: opportunityCount, isLoading: opportunityCountLoading } =
    useRegionOpportunityCount({
      token,
      regionIds: counterRegionIds,
      acceptsGamblingProceeds: !form.refuseGambling,
      acceptsInternational: form.acceptInternational,
      iwiIds: form.showIwiInput ? form.iwi : [],
    })

  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [fieldErrors, setFieldErrors] = useState({
    orgName: '',
    location: '',
    website: '',
  })
  const [duplicate, setDuplicate] = useState<DuplicateState>(INITIAL_DUPLICATE)
  const [requesting, setRequesting] = useState(false)
  const [switchingOrg, setSwitchingOrg] = useState(false)
  const [goToOrgError, setGoToOrgError] = useState<string | null>(null)
  const initialFormRef = useRef(JSON.stringify(form))
  const websiteChangedRef = useRef(false)
  // Seed wizardData.orgForm from DB org record on mount.
  // Ensures the form reflects current DB state, not a stale localStorage snapshot.
  const seededRef = useRef(false)
  useEffect(() => {
    if (!seededRef.current && existingOrg) {
      if (readWizardData().orgForm) return // wizard state already present, skip DB seed
      seededRef.current = true
      const orgForm = buildFormFromOrg(existingOrg)
      setWizardData({ orgForm })
      initialFormRef.current = JSON.stringify(orgForm)
    }
  }, [existingOrg, setWizardData])

  const shouldFocusFirstErrorRef = useRef(false)
  const orgNameRef = useRef<HTMLDivElement>(null)
  const locationRef = useRef<HTMLDivElement>(null)
  const saveErrorRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!shouldFocusFirstErrorRef.current) return
    shouldFocusFirstErrorRef.current = false
    if ((fieldErrors.orgName || duplicate.isDuplicate) && orgNameRef.current) {
      orgNameRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      })
      orgNameRef.current.querySelector<HTMLElement>('input')?.focus()
    } else if (fieldErrors.location && locationRef.current) {
      locationRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      })
      locationRef.current.querySelector<HTMLElement>('input')?.focus()
    }
  }, [fieldErrors, duplicate])

  useEffect(() => {
    if (saveError && saveErrorRef.current) {
      saveErrorRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      })
    }
  }, [saveError])

  const updateField = useCallback(
    <K extends keyof OrgFormData>(key: K, value: OrgFormData[K]) => {
      setWizardData((prev) => ({
        orgForm: { ...(prev.orgForm ?? form), [key]: value },
      }))
      if (key === 'orgName') {
        setFieldErrors((prev) => ({ ...prev, orgName: '' }))
        setDuplicate(INITIAL_DUPLICATE)
        setGoToOrgError(null)
      }
      if (key === 'regionIds')
        setFieldErrors((prev) => ({ ...prev, location: '' }))
    },
    [setWizardData, form],
  )

  // Fire page discovery in background (non-blocking).
  // Caller clears wizardData.pages synchronously before navigation
  // so step 8's hydration re-inits from the new discoveredPages.
  const discoverInBackground = (
    token: string,
    uuid: string,
    website: string,
  ) => {
    discoverPages
      .mutateAsync({ token, orgUuid: uuid, website })
      .then((pages) => {
        // Only cache if the form's website still matches (guards against blur→Next race)
        const current = readWizardData().orgForm?.website?.trim() ?? ''
        if (normalizeUrl(current) === normalizeUrl(website)) {
          setWizardData({
            discoveredPages: pages,
            discoveredForWebsite: website,
          })
        }
      })
      .catch(() => {
        // Non-critical — continue without discovered pages
      })
  }

  // Fire discovery on blur when the website URL has changed.
  // Gives background discovery a head start before step 8 mounts.
  const handleWebsiteBlur = () => {
    const current = readWizardData()
    const currentWebsite = current.orgForm?.website?.trim() ?? ''
    const prevDiscoveredFor = current.discoveredForWebsite

    if (normalizeUrl(currentWebsite) === normalizeUrl(prevDiscoveredFor)) return

    websiteChangedRef.current = true

    // Clear pending pages and stale discovery cache
    setWizardData((prev) => ({
      pages: prev.pages.filter((p) => p.status === 'ingested'),
      discoveredPages: [],
      discoveredForWebsite: '',
    }))

    // Start background discovery for the new URL
    if (currentWebsite) {
      getToken()
        .then((freshToken) => {
          discoverInBackground(freshToken, orgUuid!, currentWebsite)
        })
        .catch(() => {})
    }
  }

  const isValid =
    !saving &&
    (!duplicate.isDuplicate ||
      (duplicate.hasSubOrgs && duplicate.acknowledged))

  useStep({
    isValid,
    beforeNext: async () => {
      setSaveError('')
      setFieldErrors({ orgName: '', location: '', website: '' })

      const errors = {
        // Org name is only entered/required on the details step.
        orgName:
          section === 'details' && !form.orgName.trim()
            ? 'Please enter an organisation name'
            : '',
        // Location is optional: nationally available funding is always included,
        // so an org with no local region selected is valid (national-only).
        location: '',
        // Checked here rather than on blur because the wizard prefills this from
        // the Charities Register, where a Facebook page is a common entry — a
        // blur handler never fires for a value the user never touched.
        website:
          section === 'details' && isBlockedUrl(form.website.trim())
            ? getBlockedUrlMessage()
            : '',
      }
      if (errors.orgName || errors.location || errors.website) {
        shouldFocusFirstErrorRef.current = true
        setFieldErrors(errors)
        return false
      }

      if (!orgUuid) {
        setSaveError('Organisation not found. Please go back and try again.')
        return false
      }

      const freshToken = await getToken()

      // Only check when the name actually changed — renaming to a name that
      // collides with another org hits a DB unique constraint otherwise
      // (surfaces as a generic error rather than the friendly warning below).
      // Skipped once the umbrella message below has been acknowledged, so the
      // second Next click actually proceeds instead of re-blocking forever.
      const nameChanged =
        section === 'details' &&
        form.orgName.trim() !== (existingOrg?.name ?? '').trim()
      if (nameChanged && !duplicate.acknowledged) {
        try {
          const result = await checkDuplicate.mutateAsync({
            token: freshToken,
            params: { name: form.orgName.trim() },
          })
          if (result.exists && !result.has_sub_orgs) {
            shouldFocusFirstErrorRef.current = true
            setDuplicate({
              ...INITIAL_DUPLICATE,
              isDuplicate: true,
              orgName: result.org_name,
              orgUuid: result.org_uuid,
              // The check this hits already excludes orgs the caller belongs
              // to, so anything it reports here can never be the caller's own.
            })
            return false // block advance
          }
          if (result.exists && result.has_sub_orgs) {
            shouldFocusFirstErrorRef.current = true
            setDuplicate({
              ...INITIAL_DUPLICATE,
              isDuplicate: true,
              orgName: result.org_name,
              orgUuid: result.org_uuid,
              hasSubOrgs: true,
            })
            return false // block until acknowledged — see isValid above
          }
        } catch {
          // Fail-open: allow advance on error
        }
      }

      // Save to wizardData (localStorage) synchronously — preserves data even if API call fails
      const currentWebsite = form.website.trim()
      // websiteChangedRef is set by handleWebsiteBlur when the user actually
      // changed the URL. The discoveredForWebsite fallback covers cold starts
      // (first visit with a website but no prior discovery).
      const websiteChanged =
        !!currentWebsite &&
        (websiteChangedRef.current ||
          normalizeUrl(currentWebsite) !==
            normalizeUrl(wizardData.discoveredForWebsite))

      // If bg discovery already ran for the new website, preserve its cache
      // so step 7 gets a cache hit instead of re-running blocking discovery.
      const bgDiscoveryFresh =
        normalizeUrl(wizardData.discoveredForWebsite) ===
        normalizeUrl(currentWebsite)

      setWizardData({
        orgForm: form,
        ...(websiteChanged
          ? {
              pages: [],
              ...(bgDiscoveryFresh
                ? {}
                : { discoveredPages: [], discoveredForWebsite: '' }),
            }
          : {}),
      })

      setSaving(true)
      try {
        await updateOrg.mutateAsync({
          token: freshToken,
          editedOrg: {
            uuid: orgUuid as UUID,
            name: form.orgName.trim(),
            charities_number:
              wizardData.entityType === 'charity'
                ? wizardData.orgNumber || null
                : null,
            description: form.description.trim() || null,
            profile_text: null,
            region_ids: form.regionIds,
            // Only persist iwi when the toggle is on (matches the counter). The
            // selection stays in form state so re-enabling the toggle recovers it.
            iwi: form.showIwiInput ? form.iwi : [],
            accepts_gambling_proceeds: !form.refuseGambling,
            accepts_international: form.acceptInternational,
            website: form.website.trim() || undefined,
            early_access: false,
            active: true,
            status: null,
            membership: 'free',
            membership_until: null,
            tier: null,
            payment_type: null,
            general_info: {
              primary_contact_name: user
                ? `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim()
                : null,
              primary_contact_phone: null,
              primary_contact_email: user?.email ?? null,
              secondary_contact_name: null,
              secondary_contact_phone: null,
              secondary_contact_email: null,
              postal_address: null,
              physical_address: null,
              website: form.website.trim() || null,
            },
          },
        })
        // Website + page discovery only run from the details step (where the
        // website field lives). The locations step leaves the website untouched.
        if (section === 'details' && currentWebsite) {
          await updateWebsite.mutateAsync({
            token: freshToken,
            orgUuid: orgUuid as UUID,
            website: currentWebsite,
          })
        }
        if (section === 'details' && websiteChanged && !bgDiscoveryFresh) {
          discoverInBackground(freshToken, orgUuid, currentWebsite)
        }
      } catch (err) {
        // Not just a race: the proactive check above intentionally excludes
        // orgs the caller already belongs to (so renaming within your own
        // orgs isn't flagged as a warning), so a collision with one of your
        // own other orgs always lands here, not there — this is the normal
        // path for that case, not a rare edge. Render the same DuplicateWarning
        // the proactive check uses, not a bare field string, so this common
        // case gets the same help (org name, Request to Join) as that one.
        if (err instanceof DuplicateOrgNameError) {
          shouldFocusFirstErrorRef.current = true
          setDuplicate({
            ...INITIAL_DUPLICATE,
            isDuplicate: true,
            orgName: err.duplicate.orgName,
            orgUuid: err.duplicate.orgUuid,
            alreadyMember: err.duplicate.alreadyMember,
          })
        } else {
          setSaveError(
            err instanceof Error ? err.message : 'Failed to save organisation details',
          )
        }
        return false // Block navigation so user sees the error
      } finally {
        setSaving(false)
      }

      // Mark profile stale only if form data actually changed
      if (
        JSON.stringify(form) !== initialFormRef.current &&
        maxStepReached >= STEPS.PROFILE_GENERATING
      ) {
        setStaleFrom((prev) =>
          prev === null
            ? STEPS.PROFILE_GENERATING
            : Math.min(prev, STEPS.PROFILE_GENERATING),
        )
      }
    },
  })

  const handleRequestJoin = async () => {
    if (!duplicate.orgUuid) return
    setRequesting(true)
    try {
      const freshToken = await getToken()
      const result = await requestJoinMutation.mutateAsync({
        token: freshToken,
        orgUuid: duplicate.orgUuid,
      })
      setDuplicate((prev) => ({
        ...prev,
        requestSent: true,
        emailSent: result.email_sent,
      }))
    } catch {
      setDuplicate((prev) => ({ ...prev, requestSent: true, emailSent: false }))
    }
    setRequesting(false)
  }

  const handleAcknowledgeUmbrella = () => {
    setDuplicate((prev) => ({ ...prev, acknowledged: true }))
  }

  const handleGoToOrg = async (targetOrgUuid: string) => {
    if (!user) return
    setSwitchingOrg(true)
    setGoToOrgError(null)
    try {
      const freshToken = await getToken()
      await switchToOrg(freshToken, user, targetOrgUuid)
    } catch {
      setSwitchingOrg(false)
      setGoToOrgError("Couldn't switch organisations. Please try again.")
    }
  }

  const selectedIwi = (iwiList ?? []).filter((i: IwiData) =>
    form.iwi.includes(i.id),
  )

  return (
    <Box sx={{ py: 2 }}>
      {section === 'details' ? (
        <>
          <Typography variant="h5" sx={stepHeadingSx}>
            Organisation details
          </Typography>
          <Typography sx={stepDescriptionSx}>
            Tell us a bit more about your organisation.
          </Typography>
        </>
      ) : (
        <>
          <Typography variant="h5" sx={stepHeadingSx}>
            Funding preferences and locations
          </Typography>
          <Typography sx={stepDescriptionSx}>
            Set your funding preferences and where your organisation works.
            Nationally available funding is always included.
          </Typography>
        </>
      )}

      {saveError && (
        <Alert
          ref={saveErrorRef}
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setSaveError('')}
        >
          {saveError}
        </Alert>
      )}

      {section === 'details' && (
        <>
          <Box ref={orgNameRef}>
            <Typography sx={formLabelSx} component="label" htmlFor="orgName">
              Organisation name{' '}
              <Box component="span" aria-hidden="true">
                *
              </Box>
            </Typography>
            <TextField
              autoFocus
              fullWidth
              id="orgName"
              required
              value={form.orgName}
              onChange={(e) => updateField('orgName', e.target.value)}
              placeholder="Organisation name"
              error={!!fieldErrors.orgName}
              helperText={fieldErrors.orgName || undefined}
              sx={{ ...textFieldSx, mb: duplicate.isDuplicate ? 0.5 : 2 }}
              slotProps={{ input: { sx: inputSx } }}
            />
            <DuplicateWarning
              duplicate={duplicate}
              requesting={requesting}
              onRequestJoin={handleRequestJoin}
              onAcknowledgeUmbrella={handleAcknowledgeUmbrella}
              onGoToOrg={handleGoToOrg}
              switchingOrg={switchingOrg}
              goToOrgError={goToOrgError}
            />
          </Box>

          <Typography sx={formLabelSx} mt={duplicate.isDuplicate ? 3 : 0}>
            Website URL (optional but recommended)
          </Typography>
          <TextField
            fullWidth
            value={form.website}
            onChange={(e) => updateField('website', e.target.value)}
            onBlur={handleWebsiteBlur}
            placeholder="https://www.example.org.nz"
            error={!!fieldErrors.website}
            helperText={
              fieldErrors.website ||
              "Enter your organisation's website address (your main homepage URL only). Fundsorter will read your website and use content from it to improve your organisation profile and your funding matches. If you don't have a website, you can leave this blank."
            }
            sx={{ ...textFieldSx, mb: 2 }}
            slotProps={{ input: { sx: inputSx } }}
          />
        </>
      )}

      {section === 'locations' && (
        <>
          {/* Funding preferences — placed ABOVE the location picker (matches the
          prototype) so the opportunity counter visibly responds as they toggle. */}
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: COLORS.secondary,
              mb: 0.5,
            }}
          >
            Funding preferences
          </Typography>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 2,
              py: 1.25,
              borderTop: '1px solid #eef2f7',
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                component="label"
                htmlFor="pref-gambling"
                sx={{
                  display: 'block',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  color: COLORS.primary,
                }}
              >
                Exclude funding from gambling proceeds
              </Typography>
              <Typography variant="body2" sx={{ color: COLORS.secondary }}>
                Turn on if you do not accept funding sourced from gambling
                revenue such as pokie trusts (Lotteries, Pokies, Casinos).
              </Typography>
            </Box>
            <Switch
              id="pref-gambling"
              checked={form.refuseGambling}
              onChange={(_, checked) => updateField('refuseGambling', checked)}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 2,
              py: 1.25,
              borderTop: '1px solid #eef2f7',
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                component="label"
                htmlFor="pref-international"
                sx={{
                  display: 'block',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  color: COLORS.primary,
                }}
              >
                Accept international funding
              </Typography>
              <Typography variant="body2" sx={{ color: COLORS.secondary }}>
                Turn on to include opportunities from funders based outside New
                Zealand.
              </Typography>
            </Box>
            <Switch
              id="pref-international"
              checked={form.acceptInternational}
              onChange={(_, checked) =>
                updateField('acceptInternational', checked)
              }
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 2,
              py: 1.25,
              borderTop: '1px solid #eef2f7',
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                component="label"
                htmlFor="pref-iwi"
                sx={{
                  display: 'block',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  color: COLORS.primary,
                }}
              >
                Potentially eligible for iwi funding
              </Typography>
              <Typography variant="body2" sx={{ color: COLORS.secondary }}>
                Turn on if you wish to be matched with opportunities available
                for specific iwi. You can select multiple iwi.
              </Typography>
            </Box>
            <Switch
              id="pref-iwi"
              checked={form.showIwiInput}
              onChange={(_, checked) => updateField('showIwiInput', checked)}
            />
          </Box>

          {form.showIwiInput && (
            <Box sx={{ mb: 2, mt: 1 }}>
              <Autocomplete
                multiple
                loading={iwiLoading}
                options={iwiList ?? []}
                value={selectedIwi}
                onChange={(_, newValues) =>
                  updateField(
                    'iwi',
                    newValues.map((v) => v.id),
                  )
                }
                getOptionLabel={(option: IwiData) => option.name}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search iwi..."
                    sx={textFieldSx}
                    slotProps={{ input: { ...params.InputProps, sx: inputSx } }}
                  />
                )}
              />
            </Box>
          )}

          <Box ref={locationRef} sx={{ mt: 2 }}>
            <Typography sx={formLabelSx} component="label" htmlFor="location">
              Location
            </Typography>
            <Box sx={{ mb: 2 }}>
              <LocationSelector
                regions={regions ?? []}
                regionSets={regionSets ?? []}
                selectedIds={form.regionIds}
                onChange={(ids) => updateField('regionIds', ids)}
                mode="onboarding"
                showCounter
                opportunityCount={opportunityCount}
                opportunityCountLoading={opportunityCountLoading}
              />
            </Box>
            {fieldErrors.location && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {fieldErrors.location}
              </Alert>
            )}
          </Box>
        </>
      )}
    </Box>
  )
}
